import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Award, Printer, CheckCircle2, XCircle, Clock, FileText, User, CalendarDays, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { GetAdminComplianceFormsOutputType } from 'zite-endpoints-sdk';
import { buildComplianceCertificateHtml, saveCertificateAsPdf } from '@/lib/complianceCertificate';

type Submission = GetAdminComplianceFormsOutputType['allSubmissions'][0];

interface Props {
  submission: Submission | null;
  open: boolean;
  onClose: () => void;
}

const fmtDate = (d?: string) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; }
};
const fmtDateTime = (d?: string) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd MMM yyyy, h:mm a'); } catch { return d; }
};

const STATUS_COLORS: Record<string, string> = {
  'Signed': 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  'Quiz Passed': 'bg-primary/10 text-primary border-primary/30',
  'Declaration': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  'Study': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  'Not Started': 'bg-muted text-muted-foreground border-border',
  'Overdue': 'bg-destructive/10 text-destructive border-destructive/30',
};

function parseDeclarationResponses(raw?: string): { item: string; checked: boolean }[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* fall through */ }
  return [];
}

export default function ComplianceSubmissionModal({ submission: s, open, onClose }: Props) {
  if (!s) return null;

  const isSigned = s.status === 'Signed';
  const canPrintCert = isSigned && s.certificateEnabled;
  const declarations = parseDeclarationResponses(s.declarationResponses);

  const handlePrintCertificate = () => {
    if (!s.signedAt) return;
    const html = buildComplianceCertificateHtml({
      staffName: s.typedName || s.employeeName,
      formName: s.formName,
      formType: s.formType || '',
      quizScore: s.quizScore,
      passMark: s.passMark,
      signedAt: s.signedAt,
      role: s.employeeRole,
      department: s.employeeDepartment,
    });
    saveCertificateAsPdf(html, `Certificate-${s.employeeName.replace(/\s+/g, '-')}`);
  };

  const handleDownloadData = () => {
    const lines = [
      'COMPLIANCE SUBMISSION RECORD',
      '============================',
      '',
      `Employee: ${s.employeeName}`,
      `Email: ${s.employeeEmail || '—'}`,
      `Role: ${s.employeeRole || '—'}`,
      `Department: ${s.employeeDepartment || '—'}`,
      '',
      `Form: ${s.formName}`,
      `Form Type: ${s.formType || '—'}`,
      `Status: ${s.status}`,
      `Deadline: ${fmtDate(s.deadline)}`,
      `Next Due: ${fmtDate(s.nextDueDate)}`,
      '',
      '--- ASSESSMENT ---',
      `Quiz Score: ${s.quizScore != null ? `${s.quizScore}%` : '—'}`,
      `Pass Mark: ${s.passMark != null ? `${s.passMark}%` : '—'}`,
      `Attempts: ${s.quizAttempts ?? '—'}`,
      '',
      '--- DECLARATION ---',
      `Signed At: ${fmtDateTime(s.signedAt)}`,
      `Typed Name: ${s.typedName || '—'}`,
      `DOB Confirmation: ${s.dobConfirmation || '—'}`,
      '',
      ...(declarations.length > 0 ? [
        '--- DECLARATION ITEMS ---',
        ...declarations.map((d, i) => `${i + 1}. [${d.checked ? 'X' : ' '}] ${d.item}`),
      ] : []),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Compliance-${s.employeeName.replace(/\s+/g, '-')}-${s.formName.replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusClass = STATUS_COLORS[s.status] || STATUS_COLORS['Not Started'];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Submission Detail
          </DialogTitle>
        </DialogHeader>

        {/* Status + Certificate banner */}
        <div className="flex items-center justify-between gap-3 mt-1">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded border ${statusClass}`}>
            {s.status}
          </span>
          {isSigned && (
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Completed {fmtDateTime(s.signedAt)}
            </div>
          )}
          {!isSigned && s.deadline && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              Due {fmtDate(s.deadline)}
            </div>
          )}
        </div>

        {/* Certificate enabled notice */}
        {canPrintCert && (
          <div className="bg-primary/8 border border-primary/20 rounded-lg px-4 py-3 flex items-start gap-3">
            <Award className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">Certificate Available</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                This submission is complete and a certificate has been enabled for this form. You can print a hard copy to present to the employee.
              </p>
            </div>
          </div>
        )}

        <Separator />

        {/* Employee Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <User className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Employee</p>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="font-medium text-foreground">{s.employeeName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="font-medium text-foreground">{s.employeeEmail || '—'}</p>
            </div>
            {s.employeeRole && (
              <div>
                <p className="text-xs text-muted-foreground">Role</p>
                <p className="font-medium text-foreground">{s.employeeRole}</p>
              </div>
            )}
            {s.employeeDepartment && (
              <div>
                <p className="text-xs text-muted-foreground">Department</p>
                <p className="font-medium text-foreground">{s.employeeDepartment}</p>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Form Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Form</p>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Form Name</p>
              <p className="font-medium text-foreground">{s.formName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="font-medium text-foreground">{s.formType || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Deadline</p>
              <p className="font-medium text-foreground">{fmtDate(s.deadline)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Next Due Date</p>
              <p className="font-medium text-foreground">{fmtDate(s.nextDueDate)}</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Assessment */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assessment</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/40 rounded-lg p-3 text-center border border-border">
              <p className="text-xl font-bold text-foreground">{s.quizScore != null ? `${s.quizScore}%` : '—'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Score</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3 text-center border border-border">
              <p className="text-xl font-bold text-foreground">{s.passMark != null ? `${s.passMark}%` : '—'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Pass Mark</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3 text-center border border-border">
              <p className="text-xl font-bold text-foreground">{s.quizAttempts ?? '—'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Attempts</p>
            </div>
          </div>
          {s.quizScore != null && s.passMark != null && (
            <div className={`mt-2 flex items-center gap-1.5 text-xs font-semibold ${s.quizScore >= s.passMark ? 'text-emerald-600' : 'text-destructive'}`}>
              {s.quizScore >= s.passMark
                ? <><CheckCircle2 className="w-3.5 h-3.5" /> Passed</>
                : <><XCircle className="w-3.5 h-3.5" /> Did not meet pass mark</>
              }
            </div>
          )}
        </div>

        {/* Declaration */}
        {isSigned && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Declaration</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
                <div>
                  <p className="text-xs text-muted-foreground">Signed Name</p>
                  <p className="font-medium text-foreground">{s.typedName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">DOB Confirmed</p>
                  <p className="font-medium text-foreground">{s.dobConfirmation || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Signed At</p>
                  <p className="font-medium text-foreground">{fmtDateTime(s.signedAt)}</p>
                </div>
              </div>

              {declarations.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">Declaration Items</p>
                  {declarations.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      {d.checked
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        : <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      }
                      <span className={d.checked ? 'text-foreground' : 'text-muted-foreground'}>{d.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" className="flex-1 h-9" onClick={handleDownloadData}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download Record
          </Button>
          {canPrintCert && (
            <Button size="sm" className="flex-1 h-9 bg-primary text-primary-foreground" onClick={handlePrintCertificate}>
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Certificate
            </Button>
          )}
          {isSigned && !s.certificateEnabled && (
            <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground bg-muted/40 border border-border rounded-md h-9 px-3">
              Certificate not enabled for this form
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
